





















































































































































import Vue from 'vue';
import { toNumber } from 'lodash';
import {
  FormValidation,
  CloseFullscreenWrapper,
  TriggerRefreshError,
} from '@/mixins/index';

import { api, ACCOUNT_API_SUB_URLS, V2 } from '@/api';

import {
  required,
  minValue,
  maxValue,
  requiredIf,
} from 'vuelidate/lib/validators';

import {
  FullscreenWrapper,
  FullscreenContent,
  FullscreenSuccess,
  FullscreenError,
  FullscreenLoading,
} from '@/components/fullscreen-wrapper-components';

import InputMaskTypes from '@/directives/input-mask/input-mask-types';
import { ExtendMixins } from '@/services';
import {
  InvestmentAcccountFormModel,
  UserModel,
  ContentfulLink,
} from '@/@types/member';
import { Links } from '@/mixins/link-mixin';
import { getContentArea } from '@/contentful';

interface Data {
  isLoading: boolean;
  showSuccessMessage: boolean;
  successMessage: string;
  showErrorMessage: boolean;
  errorMessage: string;
  formModel: InvestmentAcccountFormModel;
  confirmBeforeClose: boolean;
  changeMade: boolean;
  termsAndConditionsLink: ContentfulLink;
}

export default ExtendMixins(
  FormValidation,
  CloseFullscreenWrapper,
  TriggerRefreshError,
  Links,
).extend({
  name: 'InvestmentsAccount',
  props: {
    params: {
      type: Object as () => any,
      required: true,
    },
  },
  data(): Data {
    return {
      isLoading: false,
      showSuccessMessage: false,
      successMessage: this.params.successMessage,
      showErrorMessage: false,
      errorMessage: this.params.errorMessage,
      formModel: {
        amount: '',
        percentage: 0,
        terms: false,
        linkedTerms: false,
      },
      confirmBeforeClose: true,
      changeMade: false,
      termsAndConditionsLink: null,
    };
  },
  beforeMount() {
    this.constructShowErrorModel('formModel');
    getContentArea([
      'terms-and-conditions-investment-accounts',
    ])
    .then((contentAreas) => {
      if (contentAreas) {
        if (contentAreas[0] && contentAreas[0].fields) {
          this.termsAndConditionsLink = this.parseContentfulLink(contentAreas[0].fields);
          this.$nextTick(() => {
            this.setLinksClickTrackerFunctionality('termsAndConditionsDiv');
          });
        }
      }
    });
  },
  mounted() {
    if (!this.params.changeContribution) {
      (this.$refs.amountField as any).focus();
    } else if (!this.hasInvestment || this.params.changeContribution) {
      this.formModel.percentage = this.params.account.investmentFutureContributionPercentage;
      (this.$refs.percentageField as any).focus();
    }
  },
  validations: {
    formModel: {
      amount: {
        required: requiredIf(function(this: any): boolean {
          return !this.params.changeContribution;
        }),
        minValue: minValue(0.01),
        maxValue(this: any, value: string): boolean {
          return (maxValue(
            this.params.account.availableBalance -
              this.params.account.investmentThreshold,
          ) as any)(value);
        },
      },
      percentage: {
        maxValue: maxValue(100),
        minValue: minValue(0),
        required,
      },
      terms: {
        required: requiredIf(function(this: any): boolean {
          return this.showTermsAndConditions;
        }),
      },
      linkedTerms: {
        required: requiredIf(function(this: any): boolean {
          return this.showLinkedTermsAndConditions;
        }),
      },
    },
  },
  computed: {
    subheadlineText(): string {
      if (!this.hasInvestment) {
        return 'investment-account-transfer-subheadline_open-account';
      }
      return this.params.changeContribution
        ? 'investment-account-transfer-subheadline_change-contribution'
        : 'investment-account-transfer-subheadline_request-transfer';
    },
    showLinkedTermsAndConditions(): boolean {
      return !this.hasInvestment;
    },
    showTermsAndConditions(): boolean {
      return (
        !this.hasInvestment ||
        (this.hasInvestment && !this.params.changeContribution)
      );
    },
    termsAndConditions(): string {
      return !this.hasInvestment
        ? 'investment-transfer_form-label_terms-and-conditions'
        : 'investment-transfer_form-label_terms-and-conditions_request-transfer';
    },
    availableAmount(): number {
      return this.$options.filters.currency(
        this.params.account.availableBalance -
          this.params.account.investmentThreshold,
      );
    },
    hasInvestment(): boolean {
      return this.params.account.hasInvestmentAccount;
    },
    user(): UserModel {
      return this.$store.getters.user;
    },

    // VALIDATIONS
    amountErrors(): string[] {
      const formField = 'formModel.amount';
      return this.FormValidationMixin.constructErrors(formField, [
        this.FormValidationMixin.validateField(
          formField,
          [
            'submit-claim_form-error_amount_required',
            'global_form-error_default_required',
          ],
          'required',
        ),
        this.FormValidationMixin.validateField(
          formField,
          'submit-claim_form-error_amount_min-value',
          'minValue',
        ),
        this.FormValidationMixin.validateField(
          formField,
          'investment-account-transfer_form-error_transfer-amount-greater-available',
          'maxValue',
        ),
      ]);
    },
    percentageErrors(): string[] {
      const formField = 'formModel.percentage';
      return this.FormValidationMixin.constructErrors(formField, [
        this.FormValidationMixin.validateField(
          formField,
          [
            'investment-account-transfer_form-error_contribution-percent-required',
            'global_form-error_default_required',
          ],
          'required',
        ),
        this.FormValidationMixin.validateField(
          formField,
          'investment-account-transfer_form-error_contribution-percent-greater-100',
          'maxValue',
        ),
        this.FormValidationMixin.validateField(
          formField,
          'investment-account-transfer_form-error_contribution-percent-less-0',
          'minValue',
        ),
      ]);
    },
    termsErrors(): string[] {
      const formField = 'formModel.terms';
      return this.FormValidationMixin.constructErrors(formField, [
        this.FormValidationMixin.validateField(
          formField,
          [
            'submit-claim_form-error_terms-and-conditions_required',
            'global_form-error_terms-and-conditions_required',
            'global_form-error_default_required',
          ],
          'required',
        ),
      ]);
    },
    linkedTermsErrors(): string[] {
      const formField = 'formModel.linkedTerms';
      return this.FormValidationMixin.constructErrors(formField, [
        this.FormValidationMixin.validateField(
          formField,
          [
            'submit-claim_form-error_linked-terms-and-conditions_required',
            'global_form-error_terms-and-conditions_required',
            'global_form-error_default_required',
          ],
          'required',
        ),
      ]);
    },
  },
  methods: {
    tryAgain(): void {
      this.showErrorMessage = false;
      this.confirmBeforeClose = true;
    },
    deconstructPercentage(): void {
      this.formModel.percentage = InputMaskTypes.percent.deconstruct(
        this.formModel.percentage,
      );
    },
    deconstructCurrency(): void {
      this.formModel.amount = InputMaskTypes.currency.deconstruct(
        this.formModel.amount,
      );
    },
    refreshInvestments(): void {
      this.$store.dispatch('getAccountDetail', this.params.account.accountId);
      this.$store
        .dispatch('getAccounts', this.$route.params.id)
        .then(() => {
          this.isLoading = false;
          this.showSuccessMessage = true;
          this.confirmBeforeClose = false;
        })
        .catch(() => {
          this.isLoading = false;
          this.triggerRefreshError({
            message: [
              'investments_text_refresh-data-error-message',
              'global_connection-error',
            ],
          });
        });
    },
    validateInvestment(): void {
      this.isLoading = true;

      if (this.params.changeContribution) {
        // REST-072 - Update Account Contribution Percentage
        api
          .patch(
            `${ACCOUNT_API_SUB_URLS.accounts}/${
              this.params.account.accountId
            }/invest-contribution-percentage${V2}`,
            {
              percentage: this.formModel.percentage,
            },
          )
          .then(() => {
            this.refreshInvestments();

            // GTM Event - Change Contribution
            window.dataLayer.push({
              event: 'changeInvestmentContribution',
            });
          })
          .catch(() => {
            this.isLoading = false;
            this.showErrorMessage = true;
            this.confirmBeforeClose = false;
          });
      } else {
        const payload = {
          amount: toNumber(this.formModel.amount),
        };
        // REST 037 - Add Investment Transaction
        api
          .post(
            `${ACCOUNT_API_SUB_URLS.accounts}/${
              this.params.account.accountId
            }/investments${V2}`,
            payload,
          )
          .then(() => {
            if (!this.hasInvestment) {
              const contributionPercentage = this.formModel.percentage;
              // REST-072 - Update Account Contribution Percentage (if new)
              api
                .patch(
                  `${ACCOUNT_API_SUB_URLS.accounts}/${
                    this.params.account.accountId
                  }/invest-contribution-percentage${V2}`,
                  {
                    percentage: contributionPercentage,
                  },
                )
                .then(() => {
                  this.refreshInvestments();

                  // GTM Event - Add Contribution
                  window.dataLayer.push({
                    event: 'investmentContribution',
                  });
                })
                .catch(() => {
                  this.isLoading = false;
                  this.showErrorMessage = true;
                  this.confirmBeforeClose = false;
                });
            } else {
              this.isLoading = false;
              this.showSuccessMessage = true;
              this.confirmBeforeClose = false;
            }
          })
          .catch(() => {
            this.isLoading = false;
            this.showErrorMessage = true;
            this.confirmBeforeClose = false;
          });
      }
    },
  },
  components: {
    FullscreenWrapper,
    FullscreenContent,
    FullscreenSuccess,
    FullscreenError,
    FullscreenLoading,
  },
});
